import type { SeedTables } from '../types/localDb.types'
import { type RoleCode, roleId, seedAudit, USER_IDS } from './platform.seed'
import { SEED_GROUP, seedId } from './seedId'

/**
 * Notifikasi demo GEN-03 (BA 12A JAL-PG-40-002 §15 + prototype v7.50 `core.notifications`) — beberapa per persona,
 * waktunya relatif terhadap saat seed dibangun supaya daftar, badge, dan filter periode selalu hidup.
 * Entitas `notification` mengikuti ERD v010; `severity`, `ref_type`, `ref_id` = usulan D53.
 */

const route = (path: string) => `/solutions/wms/${path}`

/** [persona, role persona, jenis, judul, isi, severity, rute, menit lalu, sudah dibaca]. */
type Demo = [string, RoleCode, string, string, string, string, string, number, boolean]

const DEMO: Demo[] = [
  /* Admin WH RM */
  [
    'yulia',
    'admin_wh_rm',
    'SYSTEM_INFO',
    'RM Quarantine QRT-260815-02 sudah 3 hari',
    'Warning Level 1 — jadwalkan QC barang karantina ini.',
    'warning',
    route('inbound-rm/qc-scheduler'),
    38,
    false
  ],
  [
    'yulia',
    'admin_wh_rm',
    'REQUEST_SUBMITTED',
    'Request material menunggu penugasan',
    'Tim Produksi mengirim request material SPK 41-08 — buka Request Material.',
    'info',
    route('request-production/request-material'),
    95,
    false
  ],
  [
    'yulia',
    'admin_wh_rm',
    'RO_STATUS',
    'RO disetujui Purchasing',
    'RO retur supplier disetujui — tugaskan operator pengambilan.',
    'success',
    route('return-order-rm/daftar-ro'),
    26 * 60,
    false
  ],
  [
    'yulia',
    'admin_wh_rm',
    'TOBACCO_RECEIPT_STATUS',
    'Penerimaan tembakau disetujui',
    'Penerimaan tembakau disetujui — terbitkan stiker dan tugaskan putaway.',
    'success',
    route('procurement/inbound-receiving'),
    50 * 60,
    true
  ],
  [
    'yulia',
    'admin_wh_rm',
    'ARRIVAL_PALLET_CLOSED',
    'Pallet PLT-000216 masuk karantina',
    'Pallet PLT-000216 ditutup (3 batch) — jadwalkan QC barang karantina.',
    'info',
    route('inbound-rm/qc-scheduler'),
    3 * 60,
    false
  ],
  [
    'yulia',
    'admin_wh_rm',
    'PR_APPROVED',
    'PR 002/PR/08/2026 disetujui',
    'PR disetujui penuh oleh Purchasing.',
    'success',
    route('procurement/daftar-pr'),
    5 * 60,
    false
  ],
  [
    'yulia',
    'admin_wh_rm',
    'DISCREPANCY_DECIDED',
    'Selisih DSC-260818-002 diputuskan',
    'Selisih opname diputuskan Kadiv WH RM — buka Selisih Opname.',
    'info',
    route('stock-opname/selisih'),
    3 * 24 * 60,
    true
  ],
  /* Supervisor RM */
  [
    'galih',
    'wh_rm_supervisor',
    'PR_SUBMITTED',
    'PR 001/PR/08/2026 menunggu approval Supervisor',
    'PR diajukan Admin WH RM — buka Daftar Purchase Request.',
    'info',
    route('procurement/daftar-pr'),
    22,
    false
  ],
  [
    'galih',
    'wh_rm_supervisor',
    'TRANSFER_PENDING_APPROVAL',
    'Transfer ITR-260817-001 menunggu approval',
    'Transfer menunggu approval Surat Jalan Internal Supervisor RM.',
    'info',
    route('wip-transfer/kirim-antar-gudang'),
    74,
    false
  ],
  [
    'galih',
    'wh_rm_supervisor',
    'SYSTEM_INFO',
    'RM Quarantine QRT-260815-02 sudah 3 hari',
    'Warning Level 1 — pantau QC barang karantina.',
    'warning',
    route('inbound-rm/qc-scheduler'),
    38,
    true
  ],
  /* Purchasing */
  [
    'rudi',
    'purchasing',
    'PR_PENDING_PURCHASING',
    'PR 001/PR/08/2026 menunggu approval Purchasing',
    'PR sudah disetujui Supervisor RM — pilih supplier dan setujui di Approval PR.',
    'info',
    route('procurement/approval-pr'),
    12,
    false
  ],
  [
    'rudi',
    'purchasing',
    'RO_PENDING_APPROVAL',
    'RO menunggu approval & jadwal retur',
    'RO disetujui Kadiv — tetapkan rencana tanggal retur.',
    'info',
    route('return-order-rm/daftar-ro'),
    140,
    false
  ],
  [
    'rudi',
    'purchasing',
    'PR_APPROVED',
    'PR 002/PR/08/2026 selesai disetujui',
    'PR disetujui penuh dan siap dipesan ke supplier.',
    'success',
    route('procurement/daftar-pr'),
    30 * 60,
    true
  ],
  /* Kadiv RM */
  [
    'alex',
    'kadiv_wh_rm',
    'DISCREPANCY_ESCALATED',
    'Selisih RM menunggu approval write-off',
    'Selisih opname RM lewat window H+5 — perlu keputusan Kadiv.',
    'critical',
    route('stock-opname/selisih'),
    55,
    false
  ],
  [
    'alex',
    'kadiv_wh_rm',
    'TRANSFER_PENDING_APPROVAL',
    'Transfer menunggu approval Kadiv',
    'Transfer disetujui Supervisor — perlu keputusan Kadiv WH RM.',
    'info',
    route('wip-transfer/kirim-antar-gudang'),
    3 * 60,
    false
  ],
  [
    'alex',
    'kadiv_wh_rm',
    'SYSTEM_INFO',
    'Stock Opname RM — Area T dijadwalkan minggu ini',
    'Jadwal opname RM sudah ditetapkan Admin WH RM.',
    'info',
    route('stock-opname/daftar-sesi'),
    28 * 60,
    true
  ],
  /* Operator WH RM */
  [
    'karno',
    'operator_wh_rm',
    'QC_ASSIGNED',
    'Jadwal QC baru untuk Anda',
    'Anda ditugaskan QC barang karantina — buka QC RM — Operator.',
    'info',
    route('inbound-rm/qc-rm-operator'),
    18,
    false
  ],
  [
    'karno',
    'operator_wh_rm',
    'PICKING_ASSIGNED',
    'Tugas picking SPK 41-08 untuk Anda',
    '3 unit picking ditugaskan — buka Picking RM — Operator.',
    'info',
    route('request-production/picking-rm-operator'),
    64,
    false
  ],
  [
    'karno',
    'operator_wh_rm',
    'MOVE_TASK_ASSIGNED',
    'Tugas pindah material untuk Anda',
    'Tugas pindah material dari Pendataan Awal — buka Pindah Material — Petugas.',
    'info',
    route('warehouse-setup-tat/pindah-material-petugas'),
    5 * 60,
    true
  ],
  [
    'tono',
    'operator_wh_rm',
    'PUTAWAY_ASSIGNED',
    'Tugas putaway RM untuk Anda',
    'Tugas penyimpanan tembakau ditugaskan — buka Putaway RM — Operator.',
    'info',
    route('inbound-rm/putaway-rm-operator'),
    33,
    false
  ],
  [
    'tono',
    'operator_wh_rm',
    'OPNAME_TASK_ASSIGNED',
    'Tugas opname untuk Anda',
    'Anda ditugaskan menghitung area opname — buka Opname — Operator.',
    'info',
    route('stock-opname/opname-operator'),
    4 * 60,
    true
  ],
  /* Tim Produksi */
  [
    'joko',
    'tim_produksi',
    'REQUEST_STATUS',
    'Request SPK 41-08: tugas picking diterbitkan',
    'Gudang sudah menerbitkan tugas picking untuk request Anda.',
    'info',
    route('request-production/request-material'),
    47,
    false
  ],
  [
    'joko',
    'tim_produksi',
    'REQUEST_STATUS',
    'Request SPK 41-07 siap diserahterimakan',
    'Picking selesai — konfirmasi di Serah Terima ke Produksi.',
    'success',
    route('request-production/serah-terima-produksi'),
    6 * 60,
    false
  ],
  /* Admin WH Sansugi */
  [
    'agus',
    'admin_wh_sansugi',
    'TRANSFER_STATUS',
    'Transfer ITR-260817-004 sudah berangkat',
    'Tunggu barangnya lalu tekan Terima Barang di Kirim Barang Antar Gudang.',
    'info',
    route('wip-transfer/kirim-antar-gudang'),
    80,
    false
  ],
  [
    'agus',
    'admin_wh_sansugi',
    'TRANSFER_STATUS',
    'Transfer ITR-260816-002 disetujui',
    'Transfer disetujui penuh — Admin WH RM akan menugaskan picking.',
    'success',
    route('wip-transfer/kirim-antar-gudang'),
    22 * 60,
    true
  ],
  /* Admin WH FG */
  [
    'ulil',
    'admin_wh_fg',
    'ALM_REQUEST_CREATED',
    'FG Batch B-260818-S1L-TBC-XTRA-20 menunggu Terima & Cek',
    'Batch Shift 1 (LINK) menunggu Terima & Cek Admin WH FG.',
    'info',
    route('inbound-fg/penerimaan-fg'),
    17,
    false
  ],
  [
    'ulil',
    'admin_wh_fg',
    'DISCREPANCY_DECIDED',
    'Write-off selisih FG ditolak',
    'Kadiv WH FG menolak write-off — investigasi dilanjutkan.',
    'warning',
    route('stock-opname/selisih'),
    2 * 60 + 20,
    false
  ],
  [
    'ulil',
    'admin_wh_fg',
    'CLAIM_AUTO_APPROVED',
    'Claim disetujui otomatis',
    'Hasil sortir claim disetujui sistem karena melewati batas waktu approval.',
    'warning',
    route('customer-claim/daftar-claim'),
    27 * 60,
    true
  ],
  /* Operator WH FG */
  [
    'budi',
    'operator_wh_fg',
    'PICKING_ASSIGNED',
    'Tugas picking FG untuk Anda',
    'Tugas picking order pengiriman — buka Picking FG — Operator.',
    'info',
    route('outbound-fg/picking-fg-operator'),
    9,
    false
  ],
  [
    'budi',
    'operator_wh_fg',
    'CLAIM_SORT_ASSIGNED',
    'Tugas sortir claim untuk Anda',
    'Tugas sortir claim ditugaskan — buka Claim — Operator.',
    'info',
    route('customer-claim/claim-operator'),
    58,
    false
  ],
  [
    'budi',
    'operator_wh_fg',
    'DESTRUCTION_ASSIGNED',
    'Tugas pemusnahan untuk Anda',
    'Pindahkan barang ke Area Pemusnahan — buka Pemusnahan — Operator.',
    'info',
    route('destruction-fg/pemusnahan-operator'),
    7 * 60,
    true
  ],
  [
    'sari',
    'operator_wh_fg',
    'PUTAWAY_ASSIGNED',
    'Tugas putaway FG untuk Anda',
    'Tugas putaway FG ditugaskan — buka Putaway FG — Operator.',
    'info',
    route('inbound-fg/putaway-fg-operator'),
    41,
    false
  ],
  [
    'sari',
    'operator_wh_fg',
    'OPNAME_STARTED',
    'Sesi opname FG dimulai',
    'Anda mendapat tugas hitung — buka Opname — Operator.',
    'info',
    route('stock-opname/opname-operator'),
    9 * 60,
    true
  ],
  /* Supervisor FG */
  [
    'hendra',
    'supervisor_wh_fg',
    'ALM_PENDING_APPROVAL',
    'Batch FG menunggu approval Supervisor',
    'Batch sudah dicek Admin — perlu approval Supervisor WH FG.',
    'info',
    route('inbound-fg/terima-cek-fg'),
    14,
    false
  ],
  [
    'hendra',
    'supervisor_wh_fg',
    'CLAIM_AWAITING_APPROVAL',
    'Claim menunggu approval hasil sortir (D2)',
    'Hasil sortir claim dikunci operator — perlu approval.',
    'info',
    route('customer-claim/daftar-claim'),
    90,
    false
  ],
  [
    'hendra',
    'supervisor_wh_fg',
    'DESTRUCTION_PENDING_APPROVAL',
    'Pemusnahan FG menunggu approval',
    'Dokumen pemusnahan FG menunggu persetujuan Supervisor FG.',
    'info',
    route('destruction-fg/dokumen-pemusnahan'),
    5 * 60,
    true
  ],
  /* Kadiv FG */
  [
    'ratna',
    'kadiv_wh_fg',
    'DISCREPANCY_ESCALATED',
    'URGENT: selisih FG lewat window H+0',
    'Menunggu approval write-off Anda.',
    'critical',
    route('stock-opname/selisih'),
    31,
    false
  ],
  [
    'ratna',
    'kadiv_wh_fg',
    'ALM_AUTO_APPROVED',
    'Batch FG disetujui otomatis',
    'Batch disetujui sistem karena melewati batas waktu approval.',
    'warning',
    route('inbound-fg/penerimaan-fg'),
    4 * 60,
    true
  ],
  /* Sales / Marketing */
  [
    'rina',
    'sales_marketing',
    'SYSTEM_INFO',
    'Rencana pengiriman minggu ini sudah dirilis',
    'Admin WH FG merilis order pengiriman — pantau di Order Pengiriman FG.',
    'info',
    route('outbound-fg/order-pengiriman'),
    2 * 60,
    false
  ],
  [
    'rina',
    'sales_marketing',
    'SYSTEM_INFO',
    'Claim customer diproses',
    'Claim customer Anda sedang disortir Gudang FG.',
    'info',
    route('customer-claim/daftar-claim'),
    29 * 60,
    true
  ]
]

export function buildWmsNotificationSeed(): SeedTables {
  const now = Date.now()
  return {
    notification: DEMO.map(([persona, role, type, title, message, severity, link, minutesAgo, read], index) => {
      const created = new Date(now - minutesAgo * 60_000).toISOString()
      return {
        ...seedAudit,
        id: seedId(SEED_GROUP.notification, index + 1),
        date_created: created,
        user_id: USER_IDS[persona],
        role_id: roleId(role),
        type,
        title,
        message,
        link,
        channel: 'IN_APP',
        read_at: read ? new Date(now - Math.max(minutesAgo - 5, 1) * 60_000).toISOString() : null,
        dedupe_key: null,
        attempt_count: 1,
        last_attempted_at: created,
        delivery_result: 'DELIVERED',
        severity,
        ref_type: null,
        ref_id: null
      }
    })
  }
}
